// §9.6 / §14 — the installed-app registry.
//
// Install fetches `/.well-known/hangar-app.json` from an origin, validates it against §9.2, and
// caches the manifest and the icon locally. Everything downstream — the launcher, the taskbar, the
// file-type registry, the Store — reads from here, so an app is "installed" in exactly one sense.
//
// Two things are deliberately *not* here: the bridge (an installed app is a manifest, not a running
// frame) and the health check (§9.6's `HEAD` loop, M8.7), which only annotates what this owns.
import {
  DEFAULT_GRANTED_PERMISSIONS,
  manifestUrlFor,
  parseManifest,
  type AppManifest,
  type ManifestProblem,
  type Permission,
} from '@hangar/shared';

export type AppHealth = 'ok' | 'unhealthy';

export interface InstalledApp {
  readonly id: string;
  readonly manifest: AppManifest;
  readonly installedAt: number;
  /** An `<svg>` string: the manifest's icon if it had one, otherwise generated (D-043). */
  readonly icon: string;
  /** §9.4 — what this app has actually been granted, not what it asked for. */
  readonly permissions: readonly Permission[];
  readonly health: AppHealth;
  /** §9.6 — consecutive failed health checks. Three marks it unhealthy. */
  readonly failures: number;
}

/** What is written to disk. The icon is cached so an offline launcher still has one. */
interface StoredApp {
  manifest: AppManifest;
  installedAt: number;
  icon: string;
  permissions: Permission[];
  health: AppHealth;
  failures: number;
}

export interface InstalledAppsStore {
  read(): Promise<StoredApp[]>;
  write(apps: StoredApp[]): Promise<void>;
}

export type InstallResult =
  | { ok: true; app: InstalledApp; replaced: boolean }
  | { ok: false; problems: ManifestProblem[] };

/** The catalog fields a builtin install needs. Narrowed so `kernel` need not import the catalog. */
export interface BuiltinInstall {
  id: string;
  name: string;
  permissions?: readonly string[];
}

export interface AppRegistryOptions {
  store: InstalledAppsStore;
  /** Injected so tests need no network and the Store can pass its own cache. */
  fetchJson: (url: string) => Promise<unknown>;
  /** D-043 — the deterministic icon for an app that ships none. */
  generateIcon: (appId: string) => string;
  now?: () => number;
  /** Told whenever the installed set changes, so the launcher and bar re-read it. */
  onChange?: () => void;
}

export interface AppRegistry {
  /** Load what was installed last time. Safe to call once at boot. */
  hydrate(): Promise<void>;
  list(): InstalledApp[];
  get(appId: string): InstalledApp | undefined;
  has(appId: string): boolean;
  /** Fetch, validate and install the app at `origin`. Re-installing replaces in place. */
  install(origin: string): Promise<InstallResult>;
  /**
   * §11.5 (D-203/D-204) — install an in-process app from a `builtin` catalog entry.
   *
   * No fetch, no manifest parse, no §9.6 health scheduling: the code shipped in this bundle, so
   * there is nothing to validate an origin against and nothing to `HEAD`. A synthetic manifest is
   * stored so the launcher, the bar and App Inspector see one shape of installed app rather than
   * two — the alternative is every consumer learning to ask "but which kind is it".
   */
  installBuiltin(entry: BuiltinInstall): Promise<InstallResult>;
  /** Install from a manifest already in hand — the Store's bundled catalog (D-040). */
  installManifest(manifest: unknown, fetchedFrom: string): Promise<InstallResult>;
  uninstall(appId: string): Promise<boolean>;
  /** §9.6 — record the outcome of a health check. Three consecutive failures dim the icon. */
  recordHealth(appId: string, healthy: boolean): Promise<AppHealth | null>;
  /** §9.4 — grant a permission the user approved through a §19.4 prompt. */
  grant(appId: string, permission: Permission): Promise<void>;
}

/** §9.6 — three consecutive failures mark an app unhealthy. */
export const HEALTH_STRIKES = 3;

/** Resolve a manifest icon `src` against the app's origin. */
export function iconUrlFor(manifest: AppManifest): string | null {
  const best = [...(manifest.icons ?? [])].sort((a, b) => b.size - a.size)[0];
  if (!best) return null;
  try {
    return new URL(best.src, manifest.origin).toString();
  } catch {
    return null;
  }
}

export function createAppRegistry(options: AppRegistryOptions): AppRegistry {
  const now = options.now ?? (() => Date.now());
  const apps = new Map<string, StoredApp>();
  let hydrated = false;

  const view = (stored: StoredApp): InstalledApp => ({
    id: stored.manifest.id,
    manifest: stored.manifest,
    installedAt: stored.installedAt,
    icon: stored.icon,
    permissions: stored.permissions,
    health: stored.health,
    failures: stored.failures,
  });

  async function persist(): Promise<void> {
    await options.store.write([...apps.values()]);
    options.onChange?.();
  }

  async function adopt(manifest: AppManifest, fetchedFrom: string): Promise<InstallResult> {
    const existing = apps.get(manifest.id);
    // An icon URL is *referenced*, not inlined — the frame's origin serves it, and an app that
    // ships none gets the deterministic generated one (D-043).
    const icon = iconUrlFor(manifest) ?? options.generateIcon(manifest.id);
    const stored: StoredApp = {
      manifest,
      installedAt: existing?.installedAt ?? now(),
      icon,
      // Re-installing keeps whatever the user has already approved; a fresh install gets §9.4's
      // defaults, intersected with what the manifest actually asks for.
      permissions:
        existing?.permissions ??
        DEFAULT_GRANTED_PERMISSIONS.filter((permission) =>
          (manifest.permissions ?? []).includes(permission),
        ),
      // A re-install is a fresh chance: whatever was wrong before, this manifest just loaded.
      health: 'ok',
      failures: 0,
    };
    apps.set(manifest.id, stored);
    await persist();
    void fetchedFrom;
    return { ok: true, app: view(stored), replaced: existing !== undefined };
  }

  return {
    async hydrate() {
      if (hydrated) return;
      hydrated = true;
      try {
        for (const stored of await options.store.read()) {
          // Re-validate on the way in: a cache written by an older build, or edited by hand, must
          // not become an installed app nobody checked.
          const result = parseManifest(stored.manifest, stored.manifest.origin);
          if (result.ok) apps.set(result.manifest.id, { ...stored, manifest: result.manifest });
        }
      } catch {
        // A registry that will not load is an empty desktop, never a broken one.
      }
      options.onChange?.();
    },

    list: () =>
      [...apps.values()]
        .map(view)
        .sort((a, b) => a.manifest.name.localeCompare(b.manifest.name)),

    get: (appId) => {
      const stored = apps.get(appId);
      return stored ? view(stored) : undefined;
    },

    has: (appId) => apps.has(appId),

    async install(origin) {
      const url = manifestUrlFor(origin);
      let json: unknown;
      try {
        json = await options.fetchJson(url);
      } catch (error) {
        return {
          ok: false,
          problems: [{ path: '<root>', message: `could not fetch ${url}: ${String(error)}` }],
        };
      }
      return this.installManifest(json, url);
    },

    async installBuiltin(entry) {
      const manifest = {
        schema: 1,
        id: entry.id,
        name: entry.name,
        version: '1.0.0',
        // A builtin has no origin (D-203). `about:builtin` is a deliberate non-URL: nothing may
        // ever fetch it, and a plausible-looking https origin would invite something to try.
        origin: 'about:builtin',
        entry: 'about:builtin',
        permissions: [...(entry.permissions ?? [])],
      } as unknown as AppManifest;
      return adopt(manifest, 'about:builtin');
    },

    async installManifest(manifest, fetchedFrom) {
      const result = parseManifest(manifest, fetchedFrom);
      if (!result.ok) return { ok: false, problems: result.problems };
      return adopt(result.manifest, fetchedFrom);
    },

    async uninstall(appId) {
      if (!apps.delete(appId)) return false;
      await persist();
      return true;
    },

    async recordHealth(appId, healthy) {
      const stored = apps.get(appId);
      if (!stored) return null;
      const failures = healthy ? 0 : stored.failures + 1;
      const health: AppHealth = failures >= HEALTH_STRIKES ? 'unhealthy' : 'ok';
      if (failures === stored.failures && health === stored.health) return health;
      apps.set(appId, { ...stored, failures, health });
      await persist();
      return health;
    },

    async grant(appId, permission) {
      const stored = apps.get(appId);
      if (!stored || stored.permissions.includes(permission)) return;
      apps.set(appId, { ...stored, permissions: [...stored.permissions, permission] });
      await persist();
    },
  };
}

/** §7.1 — where a guest's installed set lives. The kernel's own area under `/System` (D-065). */
export const INSTALLED_APPS_PATH = '/System/apps.json';

/** An `InstalledAppsStore` over the kernel's `/System`-writable view. */
export function createVfsAppsStore(vfs: {
  readText(path: string): Promise<string>;
  write(path: string, data: string): Promise<unknown>;
  exists(path: string): Promise<boolean>;
}): InstalledAppsStore {
  return {
    async read() {
      if (!(await vfs.exists(INSTALLED_APPS_PATH))) return [];
      const parsed: unknown = JSON.parse(await vfs.readText(INSTALLED_APPS_PATH));
      return Array.isArray(parsed) ? (parsed as StoredApp[]) : [];
    },
    async write(apps) {
      await vfs.write(INSTALLED_APPS_PATH, JSON.stringify(apps));
    },
  };
}
